import { useApolloClient } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import {
  type SmsConsentAdminFieldsFragment,
  type SmsConsentAdminFilterInput,
  type SmsConsentAdminListQuery,
  SmsConsentAdminListDocument,
  type SmsConsentAdminListQueryVariables,
  useSmsConsentAdminListQuery,
} from "../../generated/graphql";

export const SMS_CONSENT_PAGE_SIZE = 50;

type OlderPages = {
  /** The first page these follow; a new filter starts over. */
  readonly after: SmsConsentAdminListQuery;
  readonly rows: readonly SmsConsentAdminFieldsFragment[];
  readonly endCursor: string | null;
  readonly hasNextPage: boolean;
};

const nodesOf = (data: SmsConsentAdminListQuery) =>
  data.smsConsentAdminList.edges.map((edge) => edge.node);

/**
 * The consent list for one filter, paged with the cursor. Older pages are kept
 * here and dropped whenever the first page reloads (a new filter or search),
 * the same way the organization messages list pages.
 */
const useSmsConsentPages = (filter: SmsConsentAdminFilterInput) => {
  const client = useApolloClient();
  // Reports a GraphQL error instead of the app default of ignoring it: an
  // empty list here reads as "nobody has opted out", which is the one wrong
  // answer support must never get.
  const { data, error, loading } = useSmsConsentAdminListQuery({
    variables: { first: SMS_CONSENT_PAGE_SIZE, filter },
    errorPolicy: "none",
  });
  const [older, setOlder] = useState<OlderPages | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Tied to the first page it followed, like `older`, so a new filter never
  // shows an error from the previous one, even one that lands late.
  const [loadMoreFailure, setLoadMoreFailure] = useState<{
    readonly after: SmsConsentAdminListQuery;
    readonly message: string;
  } | null>(null);

  const current = older !== null && older.after === data ? older : null;
  const loadMoreError =
    loadMoreFailure !== null && loadMoreFailure.after === data
      ? loadMoreFailure.message
      : null;
  const rows = useMemo(
    () =>
      data === undefined
        ? []
        : [...nodesOf(data), ...(current === null ? [] : current.rows)],
    [data, current],
  );
  const pageInfo = data?.smsConsentAdminList.pageInfo;
  const hasMore = current?.hasNextPage ?? pageInfo?.hasNextPage ?? false;
  const endCursor = current?.endCursor ?? pageInfo?.endCursor ?? null;

  const loadMore = useCallback(async () => {
    if (data === undefined || endCursor === null) {
      return;
    }
    setLoadingMore(true);
    setLoadMoreFailure(null);
    try {
      const result = await client.query<
        SmsConsentAdminListQuery,
        SmsConsentAdminListQueryVariables
      >({
        query: SmsConsentAdminListDocument,
        variables: { first: SMS_CONSENT_PAGE_SIZE, after: endCursor, filter },
        fetchPolicy: "no-cache",
        // Reject on a GraphQL error, as the first page does, so the server's
        // message reaches the operator instead of a property-access error.
        errorPolicy: "none",
      });
      setOlder({
        after: data,
        rows: [...(current?.rows ?? []), ...nodesOf(result.data)],
        endCursor: result.data.smsConsentAdminList.pageInfo.endCursor ?? null,
        hasNextPage: result.data.smsConsentAdminList.pageInfo.hasNextPage,
      });
    } catch (error_) {
      setLoadMoreFailure({
        after: data,
        message: error_ instanceof Error ? error_.message : String(error_),
      });
    } finally {
      setLoadingMore(false);
    }
  }, [client, current, data, endCursor, filter]);

  return {
    rows,
    error,
    loading,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
  };
};

export default useSmsConsentPages;
